import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ComplianceAmlCheck, ComplianceWithdrawalCard } from '@shared/api/contracts/compliance';
import {
  addressBlockReasonKey,
  decisionStateKey,
  formatRelativeHold,
  formatRiskScore,
  holdReasonKey,
  makerKindKey,
  outcomeKey,
  phaseKey,
  RiskBandBadge,
} from '@entities/compliance';
import { WithdrawalStatusBadge } from '@entities/withdrawal';
import type { WithdrawalStatus } from '@shared/api/contracts';
import { formatDateTime } from '@shared/lib/format-date';
import { formatLedgerAmount } from '@shared/lib/format-ledger-amount';
import { CopyableHash, TypographyMuted, TypographySmall } from '@shared/ui';
import { useCopyToClipboard } from '@shared/lib/use-copy-to-clipboard';

export function ComplianceCardHeader({ card }: { card: ComplianceWithdrawalCard }) {
  const { t } = useTranslation('cabinet');
  const { copy } = useCopyToClipboard();
  const { withdrawal, merchant } = card;
  const relative = formatRelativeHold(withdrawal.holdSince);
  const lockedUntil = merchant.withdrawalsLockedUntil;
  const locked = Boolean(lockedUntil && new Date(lockedUntil).getTime() > Date.now());

  return (
    <div className="space-y-3 pr-8">
      <div className="flex flex-wrap items-center gap-2">
        <WithdrawalStatusBadge status={withdrawal.status as WithdrawalStatus} audience="admin" />
        <TypographySmall>{t(makerKindKey(withdrawal.makerKind))}</TypographySmall>
      </div>
      <p className="text-sm text-foreground">{t(holdReasonKey(withdrawal.holdReason))}</p>
      <p className="text-lg font-semibold tabular-nums text-foreground">
        {formatLedgerAmount(withdrawal.amount, { assetLabel: withdrawal.assetCode })}
        <span className="ml-2 text-sm font-medium text-muted-foreground">{withdrawal.network}</span>
      </p>
      <CopyableHash value={withdrawal.address} onCopy={() => copy(withdrawal.address, undefined, withdrawal.address)} />
      <TypographyMuted>
        {merchant.email} · #{merchant.id}
      </TypographyMuted>
      <TypographyMuted>
        {t(`compliance.relative.${relative.unit}`, { count: relative.count })}
        {withdrawal.holdSince ? ` · ${formatDateTime(withdrawal.holdSince)}` : ''}
      </TypographyMuted>
      {locked && lockedUntil ? (
        <p className="rounded-xl border border-pending-foreground/40 bg-pending/10 px-3 py-2 text-sm text-foreground">
          {t('compliance.merchantLocked', { datetime: formatDateTime(lockedUntil) })}
        </p>
      ) : null}
    </div>
  );
}

function SignalList({ signals }: { signals: Record<string, number> | null | undefined }) {
  const entries = Object.entries(signals ?? {});
  if (!entries.length) return null;
  return (
    <ul className="mt-2 space-y-1">
      {entries.map(([signal, share]) => (
        <li key={signal} className="flex justify-between gap-3 text-xs text-muted-foreground">
          <span className="break-all">{signal}</span>
          <span className="tabular-nums">{formatRiskScore(share)}</span>
        </li>
      ))}
    </ul>
  );
}

export function ComplianceChecks({ checks }: { checks: ComplianceAmlCheck[] }) {
  const { t } = useTranslation('cabinet');
  const [openId, setOpenId] = useState<string | null>(null);
  if (!checks.length) return <TypographyMuted>{t('compliance.checks.empty')}</TypographyMuted>;

  return (
    <ul className="space-y-2">
      {checks.map((check) => {
        const open = openId === check.id;
        return (
          <li key={check.id} className="rounded-xl border border-border px-3 py-2">
            <button
              type="button"
              className="flex w-full min-w-0 flex-col gap-2 text-left transition-colors duration-fast ease-standard sm:flex-row sm:items-center sm:justify-between"
              onClick={() => setOpenId(open ? null : check.id)}
            >
              <span className="text-sm font-medium text-foreground">{t(phaseKey(check.phase))}</span>
              <span className="text-xs text-muted-foreground">
                {t(outcomeKey(check.outcome))}
                {check.code ? ` · ${check.code}` : ''} · {formatDateTime(check.createdAt)}
              </span>
            </button>
            <div className="mt-2">
              <RiskBandBadge band={check.band} score={check.riskscore} scoreBand={check.scoreBand} />
            </div>
            {check.prohibitedSignals.length ? (
              <div className="mt-2 flex flex-wrap gap-1">
                {check.prohibitedSignals.map((signal) => (
                  <span key={signal} className="rounded-full bg-fail px-2 py-0.5 text-xs text-fail-foreground">
                    {signal}
                  </span>
                ))}
              </div>
            ) : null}
            {open ? <SignalList signals={check.signals} /> : null}
          </li>
        );
      })}
    </ul>
  );
}

export function ComplianceReports({ card }: { card: ComplianceWithdrawalCard }) {
  const { t } = useTranslation('cabinet');
  if (!card.providerAttempts.length) return <TypographyMuted>{t('compliance.reports.empty')}</TypographyMuted>;
  return (
    <ul className="space-y-2">
      {card.providerAttempts.map((attempt) => (
        <li key={attempt.id} className="rounded-xl border border-border px-3 py-2 text-sm">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="font-medium">{attempt.state}</span>
            {attempt.report?.pdfReport ? (
              <a
                className="text-sm font-medium text-foreground underline-offset-4 transition-colors duration-fast ease-standard hover:underline"
                href={attempt.report.pdfReport}
                target="_blank"
                rel="noreferrer"
              >
                {t('compliance.reports.pdf')}
              </a>
            ) : null}
          </div>
          <p className="mt-1 break-all text-xs text-muted-foreground">
            {attempt.uid || '—'}
            {attempt.errorCode ? ` · ${attempt.errorCode}` : ''}
          </p>
          <p className="text-xs text-muted-foreground">{formatDateTime(attempt.receivedAt ?? attempt.providerCheckedAt ?? '')}</p>
        </li>
      ))}
    </ul>
  );
}

export function ComplianceAddressBlock({ card }: { card: ComplianceWithdrawalCard }) {
  const { t } = useTranslation('cabinet');
  const counts = Object.entries(card.address.previousWithdrawals);
  return (
    <div className="space-y-3">
      {card.address.whitelist.length === 0 ? <TypographyMuted>{t('compliance.address.empty')}</TypographyMuted> : null}
      <ul className="space-y-2">
        {card.address.whitelist.map((item) => (
          <li key={item.id} className="rounded-xl border border-border px-3 py-2 text-sm">
            <p>{item.amlBlockedAt ? t(addressBlockReasonKey(item.amlBlockReason)) : t('compliance.address.active')}</p>
            <p className="text-xs text-muted-foreground">
              {item.lastRiskBand || '—'} · {formatRiskScore(item.lastRiskscore)} · {formatDateTime(item.lastAmlCheckedAt ?? '')}
            </p>
          </li>
        ))}
      </ul>
      {counts.length ? (
        <p className="text-xs text-muted-foreground">
          {counts.map(([status, count]) => `${status}: ${count}`).join(' · ')}
        </p>
      ) : null}
    </div>
  );
}

export function ComplianceDecisionList({ card }: { card: ComplianceWithdrawalCard }) {
  const { t } = useTranslation('cabinet');
  if (!card.decisions.length) return <TypographyMuted>{t('compliance.decisions.empty')}</TypographyMuted>;
  return (
    <ul className="space-y-2">
      {card.decisions.map((decision) => (
        <li key={decision.id} className="rounded-xl border border-border px-3 py-2 text-sm">
          <p className="font-medium">
            {t(`compliance.decisions.${decision.decision}.confirm`, { defaultValue: decision.decision })} ·{' '}
            {t(decisionStateKey(decision.state))}
          </p>
          <p className="mt-1 whitespace-pre-wrap break-words">{decision.reason}</p>
          <p className="mt-1 text-xs text-muted-foreground">
            #{decision.actorUserId}
            {decision.secondApproverUserId ? ` → #${decision.secondApproverUserId}` : ''} · {formatRiskScore(decision.riskscore)} ·{' '}
            {formatDateTime(decision.createdAt)}
          </p>
        </li>
      ))}
    </ul>
  );
}
